// Road, bridge, depot, and ruin rendering — flat planes + small markers above terrain tiles.
// Reads hasRoad / hasBridge / hasDepot / hasRuin / bridgeEdges props from tile scene
// nodes (exposed by WASM snapshot). Roads are tinted per-owner so multi-player road
// networks are visually distinct.
//
// The actual instanced-mesh components live in sibling files:
//   - InfraInstancedMesh.tsx  (roads + legacy tile bridges)
//   - DepotInstancedMesh.tsx  (supply depots)
//   - RuinInstancedMesh.tsx   (ancient ruins)
// Shared materials / geometries / tint helpers live in InfrastructureMaterials.ts.
// Edge bridges (across river channels between tiles) render as planks via EdgeBridgePlanks.

import type { SceneNode } from '@aod/protocol';
import { useMemo } from 'react';
import * as THREE from 'three';
import { DepotInstanced } from './DepotInstancedMesh.js';
import { InfraInstanced } from './InfraInstancedMesh.js';
import { bridgeMat, bridgeTint, roadMat } from './InfrastructureMaterials.js';
import { RenderLayer } from './RenderLayer.js';
import { RuinInstanced } from './RuinInstancedMesh.js';
import { buildBiomeMap, tileElevationAt } from './elevation.js';

interface InfraProps {
  readonly tiles: ReadonlyArray<SceneNode>;
}

export function Infrastructure({ tiles }: InfraProps) {
  // Filter tiles with road, bridge, depot, or ruin.
  const { roadTiles, bridgeTiles, depotTiles, ruinTiles } = useMemo(() => {
    const roads: Array<{ tile: SceneNode; idx: number }> = [];
    const bridges: Array<{ tile: SceneNode; idx: number }> = [];
    const depots: Array<{ tile: SceneNode; idx: number }> = [];
    const ruins: Array<{ tile: SceneNode; idx: number }> = [];

    tiles.forEach((tile, idx) => {
      const props = tile.props as Record<string, unknown> | undefined;
      if (props?.hasRoad) roads.push({ tile, idx });
      if (props?.hasBridge) bridges.push({ tile, idx });
      if (props?.hasDepot) depots.push({ tile, idx });
      if (props?.hasRuin) ruins.push({ tile, idx });
    });

    return { roadTiles: roads, bridgeTiles: bridges, depotTiles: depots, ruinTiles: ruins };
  }, [tiles]);

  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);

  // Edge bridges: one plank per set bit of the tile's bridgeEdges mask,
  // coloured by the carrier tile's dominion path (M5).
  const edgeBridges = useMemo(() => {
    const out: Array<{ x: number; y: number; z: number; yaw: number; tint: THREE.Color }> = [];
    for (const tile of tiles) {
      const props = tile.props as Record<string, unknown> | undefined;
      const mask = Number(props?.bridgeEdges ?? 0);
      if (!mask || !props) continue;
      const q = (props.q as number) ?? 0;
      const r = (props.r as number) ?? 0;
      const [x, , z] = tile.transform.position;
      const y = tileElevationAt(q, r, biomeMap) + 0.09;
      for (let k = 0; k < 6; k++) {
        if ((mask & (1 << k)) === 0) continue;
        const a = ((60 + 60 * k) * Math.PI) / 180;
        const nx = Math.cos(a);
        const nz = Math.sin(a);
        // Plank centred on the edge midpoint, long axis along the edge tangent.
        out.push({
          x: x + nx * 0.88,
          y,
          z: z + nz * 0.88,
          yaw: Math.atan2(-nz, nx) + Math.PI / 2,
          tint: bridgeTint(
            props.controllerArchetype as string | undefined,
            props.controllerAlignment as string | undefined,
          ),
        });
      }
    }
    return out;
  }, [tiles, biomeMap]);

  if (
    roadTiles.length === 0 &&
    bridgeTiles.length === 0 &&
    depotTiles.length === 0 &&
    ruinTiles.length === 0 &&
    edgeBridges.length === 0
  )
    return null;

  return (
    <group>
      {roadTiles.length > 0 && (
        <InfraInstanced
          entries={roadTiles}
          material={roadMat}
          yOffset={0.12}
          biomeMap={biomeMap}
          perOwnerTint
        />
      )}
      {bridgeTiles.length > 0 && (
        <InfraInstanced
          entries={bridgeTiles}
          material={bridgeMat}
          yOffset={0.18}
          biomeMap={biomeMap}
          pathTint
        />
      )}
      {edgeBridges.length > 0 && <EdgeBridgePlanks entries={edgeBridges} />}
      {depotTiles.length > 0 && <DepotInstanced entries={depotTiles} biomeMap={biomeMap} />}
      {ruinTiles.length > 0 && <RuinInstanced entries={ruinTiles} biomeMap={biomeMap} />}
    </group>
  );
}

// --- Edge bridge planks -------------------------------------------------
// One instanced draw for every bridge across a river edge. The plank spans
// the shared edge; its long axis follows the edge tangent. Planks take the
// carrier tile's dominion-path material colour via instanceColor (M5).

const plankGeo = new THREE.BoxGeometry(1.05, 0.07, 0.34);
const plankMat = new THREE.MeshBasicMaterial({ color: 'rgb(122, 90, 58)' });

function EdgeBridgePlanks({
  entries,
}: {
  readonly entries: ReadonlyArray<{
    x: number;
    y: number;
    z: number;
    yaw: number;
    tint: THREE.Color;
  }>;
}) {
  return (
    <instancedMesh
      args={[plankGeo, plankMat, entries.length]}
      renderOrder={RenderLayer.Infrastructure}
      frustumCulled
      onUpdate={(mesh) => {
        const obj = new THREE.Object3D();
        const col = new THREE.Color();
        entries.forEach((e, i) => {
          obj.position.set(e.x, e.y, e.z);
          obj.rotation.set(0, e.yaw, 0);
          obj.scale.set(1, 1, 1);
          obj.updateMatrix();
          mesh.setMatrixAt(i, obj.matrix);
          mesh.setColorAt(i, col.copy(e.tint));
        });
        mesh.count = entries.length;
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        mesh.computeBoundingSphere();
      }}
    />
  );
}
